import React, { useState, useEffect, useRef } from "react";
import { useToast } from '@chakra-ui/react';
import { InfoIcon, LinkIcon, CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  VStack,
  Input,
  Textarea,
  Select,
  Button,
  Text,
  Avatar,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  IconButton,
  Link,
  HStack,
} from "@chakra-ui/react";
import UploadCompanyLogo from "../../components/Icons/UploadCompanyLogo";
import "./Client.scss";

import { useDispatch, useSelector } from "react-redux";
import { updateClient } from "../../actions/client/clientActions";
import SectorCategoriesDropdown from "../../components/SectorCategoriesDropdown";
import { getSectorsCategories } from "../../actions/sector/sectorActions";
import { getSectorCategoryProductsApi } from "../../api/productApi";
import ProductsDropdown from "../../components/ProductsDropdown";

const ClientEditProfile = ({ clientData }) => {
  console.log("====", clientData);

  // Function to normalize locations data
  const normalizeLocations = (locations) => {
    if (!locations) return [];
    if (Array.isArray(locations)) return locations;
    if (typeof locations === 'object') {
      return Object.entries(locations).map(([type, value]) => {
        if (typeof value === 'object' && value !== null) {
          return { type, ...value };
        }
        return { type, value };
      });
    }
    return [];
  };

  const [formData, setFormData] = useState({
    companyName: clientData?.companyName || '',
    companyEmail: clientData?.companyEmail || '',
    companyWebsite: clientData?.companyWebsite || '',
    companyMission: clientData?.companyMission || '',
    companyCompliance: clientData?.companyCompliance || '',
    products: clientData?.products || [],
    locations: normalizeLocations(clientData?.locations) // Use normalized locations
  });

  const [mapUrl, setMapUrl] = useState("");
  const [userLocation, setUserLocation] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(clientData?.companyLogo);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("current");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(null);
  const [locationColors, setLocationColors] = useState({
    headquarters: { bg: "#FF6B6B", text: "white" },
    factory: { bg: "#4ECDC4", text: "white" },
    warehouse: { bg: "#FFE66D", text: "black" },
    office: { bg: "#7D5BA6", text: "white" },
    retail: { bg: "#5DA271", text: "white" },
    other: { bg: "#5D5D5D", text: "white" },
  });
  const [newLocation, setNewLocation] = useState(null);
  const [locationSearch, setLocationSearch] = useState('');
  const [locationDetails, setLocationDetails] = useState('');
  const toast = useToast();
  const [selectedLocationType, setSelectedLocationType] = useState("");
  const { loading: categoriesLoading, data: sectorData } = useSelector(
    (state) => state.sectorReducer
  );

  const { loading: locationSearchLoading, data: locationSearchData } =
    useSelector((state) => state.locationSearchReducer);

  const { loading: locationDetailsLoading, data: locationDetailsData } =
    useSelector((state) => state.locationDetailsReducer);

  const { loading: userUpdateLoading, status: userUpdateStatus } = useSelector(
    (state) => state.userUpdateReducer
  );

  const [selectedSectorCategory, setSelectedSectorCategory] = useState(
    clientData?.sectorCategory?._id
  );

  // Location Legend Component
  const LocationLegend = () => (
    <Box
      position="absolute"
      bottom="10px"
      left="10px"
      bg="white"
      p={2}
      borderRadius="md"
      boxShadow="md"
      zIndex="1"
    >
      <Text fontWeight="bold" mb={1}>Locations</Text>
      <VStack align="stretch" spacing={1}>
        {Array.isArray(formData.locations) && formData.locations.map((loc, index) => (
          <Flex
            key={index}
            align="center"
            p={1}
            borderRadius="md"
            cursor="pointer"
            bg={selectedLocationIndex === index ? "gray.100" : "transparent"}
            onClick={() => handleLocationClick(index)}
            _hover={{ bg: "gray.50" }}
          >
            <Box
              w="12px"
              h="12px"
              borderRadius="full"
              bg={locationColors[loc.type]?.bg || "#5D5D5D"}
              mr={2}
              border="2px solid white"
              boxShadow="sm"
            />
            <Text fontSize="sm">{loc.type}</Text>
          </Flex>
        ))}
      </VStack>
    </Box>
  );

  // Location Tag Component
  const LocationTag = ({ loc, index, isSelected, onClick, onRemove }) => (
    <Tooltip label={loc.value} placement="top">
      <Box
        display="flex"
        alignItems="center"
        bg={isSelected ? (locationColors[loc.type]?.bg || "gray.600") : "gray.600"}
        color={isSelected ? (locationColors[loc.type]?.text || "white") : "white"}
        px={3}
        py={1}
        borderRadius="full"
        fontSize="sm"
        cursor="pointer"
        transition="all 0.2s"
        onClick={onClick}
        _hover={{
          bg: isSelected ? (locationColors[loc.type]?.bg || "gray.500") : "gray.500",
        }}
      >
        <Text mr={2}>{loc.type}</Text>
        <Box
          as="span"
          fontSize="xs"
          ml={1}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          _hover={{ transform: "scale(1.2)" }}
        >
          ✕
        </Box>
      </Box>
    </Tooltip>
  );

  useEffect(() => {
    if (formData.locations && formData.locations.length > 0) {
      setSelectedLocationType(formData.locations[0]?.type || "");
      const firstLocation = formData.locations[0];
      if (firstLocation.lat && firstLocation.lng) {
        setMapUrl(
          `https://www.google.com/maps?q=${firstLocation.lat},${firstLocation.lng}&z=15&output=embed`
        );
      } else if (firstLocation.value) {
        const coordsMatch = firstLocation.value.match(/Lat: (.*), Lng: (.*)/);
        if (coordsMatch) {
          const lat = coordsMatch[1];
          const lng = coordsMatch[2];
          setMapUrl(
            `https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed`
          );
        } else {
          setMapUrl(
            `https://www.google.com/maps?q=${encodeURIComponent(firstLocation.value)}&z=15&output=embed`
          );
        }
      }
    }
  }, []);

  useEffect(() => {
    // Update map URL when locations change
    if (formData.locations && formData.locations.length > 0) {
      const lastLocation = formData.locations[formData.locations.length - 1];

      if (lastLocation.lat && lastLocation.lng) {
        setMapUrl(
          `https://www.google.com/maps?q=${lastLocation.lat},${lastLocation.lng}&z=15&output=embed`
        );
      } else if (lastLocation.value) {
        const coordsMatch = lastLocation.value.match(/Lat: (.*), Lng: (.*)/);

        if (coordsMatch) {
          const lat = coordsMatch[1];
          const lng = coordsMatch[2];
          setMapUrl(
            `https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed`
          );
        } else {
          // For text addresses
          setMapUrl(
            `https://www.google.com/maps?q=${encodeURIComponent(lastLocation.value)}&z=15&output=embed`
          );
        }
      }
    }
  }, [formData.locations]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ latitude, longitude });
          setMapUrl(
            `https://www.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`
          );
        },
        (error) => {
          console.error("Error fetching location:", error);
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  const handleAddLocation = () => {
    if (!selectedLocationType) {
      alert("Please select a location type");
      return;
    }

    // Check if location type already exists
    const locationTypeExists = formData.locations.some(
      (loc) => loc.type === selectedLocationType
    );

    if (locationTypeExists) {
      alert(`You can only have one ${selectedLocationType} location.`);
      return;
    }

    let newLocation;
    if (selectedOption === "current" && userLocation) {
      newLocation = {
        type: selectedLocationType,
        lat: userLocation.latitude,
        lng: userLocation.longitude,
        value: `Lat: ${userLocation.latitude}, Lng: ${userLocation.longitude}`
      };
    } else if (selectedOption === "search" && searchQuery) {
      const selectedLocation = searchResults.find((loc) => loc.display_name === searchQuery);
      if (selectedLocation) {
        newLocation = {
          type: selectedLocationType,
          value: `Lat: ${selectedLocation.lat}, Lng: ${selectedLocation.lon}`,
          lat: selectedLocation.lat,
          lng: selectedLocation.lon
        };
      } else {
        newLocation = {
          type: selectedLocationType,
          value: searchQuery,
        };
      }
    }

    if (newLocation) {
      setFormData((prevState) => ({
        ...prevState,
        locations: [...prevState.locations, newLocation],
      }));
    }

    setIsModalOpen(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleRemoveLocation = (index) => {
    if (formData.locations.length <= 1) {
      alert("You must have at least 1 location.");
      return;
    }
    setFormData((prevState) => ({
      ...prevState,
      locations: prevState.locations.filter((_, i) => i !== index),
    }));
  };

  const handleSearchLocation = async (query) => {
    setSearchQuery(query);
    if (query.trim() === "") {
      setSearchResults([]);
      return;
    }
    dispatch(locationSearch(query));
  };

  useEffect(() => {
    if (!locationSearchLoading && locationSearchData) {
      setSearchResults(locationSearchData);
    }
  }, [locationSearchLoading]);

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.description);
    dispatch(locationDetails(suggestion.place_id));
    setSearchResults([]);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setLogoFile(file);
    }
  };

  const dispatch = useDispatch();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData?.companyName === "") {
      toast.error("Please enter organization name", {
        position: "bottom-center",
        toastId: "products-mismatch",
      });
    } else {
      if (selectedSectorCategory) {
        if (
          formData?.products &&
          (formData?.products.length < 3 || formData?.products.length > 20)
        ) {
          toast.error("Please select Minimum 3 required/Maximum 20 available", {
            position: "bottom-center",
            toastId: "products-mismatch",
          });
          return;
        }
      } else {
        toast.error("Please select industry category", {
          position: "bottom-center",
          toastId: "industry-mismatch",
        });
        return;
      }
    }
    const data = new FormData();
    data.append("companyName", formData?.companyName);
    data.append("companyEmail", formData?.companyEmail);
    data.append("companyWebsite", formData?.companyWebsite);
    data.append("companyMission", formData?.companyMission);
    data.append("sectorCategory", selectedSectorCategory);
    data.append("companyCompliance", formData?.companyCompliance);
    data.append("products", JSON.stringify(formData?.products));
    data.append("locations", JSON.stringify(formData?.locations));
    if (logoFile) {
      data.append("file", logoFile);
    }
    dispatch(updateClient(clientData._id, data));
  };

  const handleSectorCategoryChange = (selectedSectorCategory) => {
    setSelectedSectorCategory(selectedSectorCategory?.value);
  };

  useEffect(() => {
    fetchSectorCategories();
  }, []);

  const fetchSectorCategories = async () => {
    dispatch(getSectorsCategories());
  };

  const handleLocationClick = (index) => {
    setSelectedLocationIndex(index);
    const location = formData.locations[index];

    if (location.lat && location.lng) {
      setMapUrl(
        `https://www.google.com/maps?q=${location.lat},${location.lng}&z=15&output=embed`
      );
    } else if (location.value) {
      const coordsMatch = location.value.match(/Lat: (.*), Lng: (.*)/);

      if (coordsMatch) {
        const lat = coordsMatch[1];
        const lng = coordsMatch[2];
        setMapUrl(
          `https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed`
        );
      } else {
        // For text addresses
        setMapUrl(
          `https://www.google.com/maps?q=${encodeURIComponent(location.value)}&z=15&output=embed`
        );
      }
    }
  };


  const [assets, setAssets] = useState([
    "https://www.amazon.com/shops/sellerID",
  ]);
  const [value, setValue] = useState("");

  const addAsset = () => {
    if (!value.trim()) return;
    setAssets([...assets, value.trim()]);
    setValue("");
  };

  const removeAsset = (url) => {
    setAssets((prev) => prev.filter((a) => a !== url));
  };








  return (
    <Box className="edit-profile">
      <form onSubmit={handleSubmit} className="profile-form">
        <Flex className="row">
          <VStack className="input-column" spacing={4} align="start">
            <Box className="input-group">
              <Input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
              />
            </Box>
            <Box className="input-group">
              <Input
                type="text"
                name="companyWebsite"
                required
                value={formData.companyWebsite}
                onChange={handleChange}
              />
            </Box>
          </VStack>
          <Box className="input-group">
            <Textarea
              name="companyMission"
              required
              value={formData.companyMission}
              onChange={handleChange}
            />
          </Box>
        </Flex>

        <Flex className="row" align="center" gap={4}>
          <Box className="input-group" flex={1}>
            <Input
              type="text"
              name="companyEmail"
              required
              value={formData.companyEmail}
              onChange={handleChange}
            />
          </Box>

          <Box flex={1}>
            <Flex align="center" gap={4}>
              <Text className="input-title">Upload Company Logo</Text>
              <Box className="input-group1">
                <input
                  type="file"
                  id="fileUpload"
                  style={{ display: "none" }}
                  onChange={handleFileUpload}
                  accept="image/*"
                />
                <label htmlFor="fileUpload">
                  <Box as="span" cursor="pointer">
                    {previewUrl ? (
                      <Avatar size="lg" src={previewUrl} alt="Preview" />
                    ) : (
                      <UploadCompanyLogo
                        className="file-icon"
                        color="#5D5D5D"
                      />
                    )}
                  </Box>
                </label>
              </Box>
            </Flex>
          </Box>
        </Flex>

        <Flex className="row">
          <Box className="input-group">
            <Text>Location</Text>
            <Flex className="location-categories" wrap="wrap" gap={2} mt={2}>
              {Array.isArray(formData.locations) && formData.locations.map((loc, index) => (
                <Box
                  key={index}
                  className="location-tag"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: " #5D5D5D",
                    padding: "5px 10px",
                    borderRadius: "15px",
                    fontSize: "14px",
                  }}
                >
                  <Text mr={2}>{loc.type}</Text>
                  <Text
                    cursor="pointer"
                    fontWeight="bold"
                    onClick={() => handleRemoveLocation(index)}
                  >
                    ✕
                  </Text>
                </Box>
              ))}
            </Flex>
            <Button
              type="button"
              className="add-location-btn"
              onClick={() => setIsModalOpen(true)}
              style={{
                background: "#5D5D5D",
                color: "white",
                borderRadius: "15px",
                padding: "10px 20px",
                fontSize: "14px",
                width: "200px",
                marginTop: "10px"
              }}
            >
              + Add New Location
            </Button>
            <Box position="relative" className="map-container" mt={4}>
              <iframe
                src={mapUrl}
                className="location-map"
                width="100%"
                height="250px"
                allowFullScreen
                loading="lazy"
              ></iframe>
              {Array.isArray(formData.locations) && formData.locations.length > 0 && <LocationLegend />}
            </Box>
          </Box>
          <VStack className="column" spacing={4} align="start">
            <Box className="input-group">
              <Flex className="row">
                <Text className="input-title">Industry Category</Text>
                <Box
                  width={{ base: "100%", md: "21rem" }}
                  pt={{ base: 2, md: "0" }}
                >
                  {categoriesLoading ? (
                    <p>Loading categories...</p>
                  ) : (
                    <SectorCategoriesDropdown
                      sectorCategories={sectorData?.data}
                      selectedSectorCategory={selectedSectorCategory}
                      handleSectorCategoryChange={handleSectorCategoryChange}
                      name={"sectorcategory"}
                    />
                  )}
                </Box>
              </Flex>
            </Box>
            <Box className="input-group">
              <Flex className="row">
                <Text className="input-title">Product Category</Text>
                {selectedSectorCategory && (
                  <Box w={{ base: "100%", md: "21rem" }}>
                    <Text fontSize={"14px"} fontWeight={500}>
                      {formData.products.length === 0
                        ? "Minimum 3 required/Maximum 20 available"
                        : formData.products.length < 3
                          ? "Minimum 3 required"
                          : "Maximum 20 available"}
                    </Text>
                    <ProductsDropdown
                      defaultProductValues={clientData?.products || []}
                      isEditable={true}
                      selectedSectorCategory={selectedSectorCategory}
                      fetchDataFunction={getSectorCategoryProductsApi}
                      placeholder="Search Products..."
                      isMulti
                      clientData={clientData}
                      handleOptionChange={(selectedOptions) => {
                        const products = selectedOptions.map(
                          (val) => val.value
                        );
                        if (formData.products.length < 20) {
                          setFormData((prev) => ({
                            ...prev,
                            products,
                          }));
                        }
                      }}
                      _focus={{
                        height: "3rem",
                        borderLeft: "5px solid #30d5c8",
                      }}
                      name={"products"}
                    />
                  </Box>
                )}
              </Flex>
            </Box>
            <Box className="input-group">
              <Flex className="row">
                <Text className="input-title">Add Compliance</Text>
                <Box className="input-group1">
                  <Input
                    type="text"
                    name="companyCompliance"
                    value={formData?.companyCompliance}
                    onChange={handleChange}
                  />
                </Box>
              </Flex>
              <VStack align="start" spacing={3} w="full" maxW="2xl">

                <HStack align="start" spacing={2} color="gray.600" fontSize="sm">
                  <InfoIcon mt="1" />
                  <Text>
                    Overtime online digital assets will help us to optimize your profile
                    for better reach
                  </Text>
                </HStack>

                {/* Existing assets list */}
                <Flex wrap="wrap" gap={2}>
                  {assets.map((url, i) => (
                    <HStack
                      key={i}
                      px={3}
                      py={2}
                      borderWidth="1px"
                      borderRadius="xl"
                      bg="gray.50"
                      spacing={2}
                    >
                      <LinkIcon />
                      <Link
                        href={url}
                        isExternal
                        maxW="360px"
                        fontSize="sm"
                        color="blue.600"
                        isTruncated
                      >
                        {url}
                      </Link>
                      <IconButton
                        size="xs"
                        aria-label="Remove"
                        icon={<CloseIcon />}
                        onClick={() => removeAsset(url)}
                        variant="ghost"
                      />
                    </HStack>
                  ))}
                </Flex>

                {/* Input and Add button */}
                <HStack w="full" spacing={2}>
                  <Input
                    type="url"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="Link To A Digital Asset"
                  />
                  <Button
                    onClick={addAsset}
                    variant="outline"
                    fontSize="sm"
                    px={10}
                    py={4}
                    borderColor="#5D5D5D"
                    color="#5D5D5D"
                  >
                    + Add New Digital Asset
                  </Button>


                </HStack>
              </VStack>


            </Box>
          </VStack>
        </Flex>
        <Button type="submit" className="save-btn">
          {userUpdateLoading ? "Processing..." : "Save Changes"}
        </Button>
      </form>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Location</ModalHeader>
          <ModalBody>
            <Select
              placeholder="Select location type"
              value={selectedLocationType}
              onChange={(e) => setSelectedLocationType(e.target.value)}
              mb={4}
            >
              <option value="headquarters">Headquarters</option>
              <option value="factory">Factory</option>
              <option value="warehouse">Warehouse</option>
              <option value="office">Office</option>
              <option value="retail">Retail</option>
              <option value="other">Other</option>
            </Select>

            <RadioGroup
              value={selectedOption}
              onChange={(value) => {
                if (value === "current") {
                  handleUseCurrentLocation();
                }
                setSelectedOption(value);
              }}
            >
              <Stack direction="column">
                <Radio value="current">Use My Current Location</Radio>
                <Radio value="search">Search Location</Radio>
              </Stack>
            </RadioGroup>

            {selectedOption === "search" && (
              <Box position="relative" mt={4}>
                <Input
                  placeholder="Search location"
                  value={searchQuery}
                  onChange={(e) => handleSearchLocation(e.target.value)}
                />
                {searchResults.length > 0 && (
                  <Box
                    position="absolute"
                    zIndex="1"
                    width="100%"
                    bg="white"
                    border="1px solid #ccc"
                    borderRadius="md"
                    mt={2}
                    maxHeight="200px"
                    overflowY="auto"
                  >
                    {searchResults.map((result, index) => (
                      <Box
                        key={index}
                        p={2}
                        cursor="pointer"
                        _hover={{ bg: "gray.100" }}
                        onClick={() => handleSuggestionClick(result)}
                      >
                        {result.description}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={handleAddLocation}
              isDisabled={!selectedLocationType}
              style={{ marginRight: "5px" }}
            >
              Add Location
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setSelectedLocationType("");
                setIsModalOpen(false);
              }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ClientEditProfile;